import { Field, useFormikContext } from "formik";
import React, { FC } from "react";
import { EncryptionTypes } from "../../../constants/encryptionTypes";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { ListQuery } from "../../../models/ListQuery";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { getValidCertificateAuthorities } from "../../../services/securityPolicyService";
import Label from "../../label/label";
import PaginatedSearchableSelectField from "../../selection-field/paginated-searchable-select-field";
import SettingSelectField from "../../settingFields/SettingSelectField";
import { WizardStepLayout } from "../../wizard/wizard-summary-layout";

interface EncryptionProps {
    onNext: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onBack: (e: React.MouseEvent<HTMLButtonElement>) => void;
    setStepsDescription: React.Dispatch<React.SetStateAction<{ details: string; encryption: string; subjectAlternativeName: string }>>;
}

const Encryption: FC<EncryptionProps> = ({ onNext, onBack, setStepsDescription }) => {
    const { translate } = useLocale();
    const { values, errors, setFieldValue } = useFormikContext<any>();
    // RSA key sizes in bits - larger sizes provide stronger security but slower performance
    const rsaKeySizesOptions: MultiSelectOption[] = [
        { value: "1024", label: "1024" }, // Minimum recommended size (legacy support)
        { value: "2048", label: "2048" }, // Most commonly used, good balance of security and performance
        { value: "4096", label: "4096" } // High security, slower performance
    ]

    // Elliptic Curve parameters - different curves offer varying levels of security
    const ecCurvesOption: MultiSelectOption[] = [
        { value: "P-256", label: "P-256" }, // NIST P-256 curve, equivalent to 3072-bit RSA
        { value: "P-384", label: "P-384" }, // NIST P-384 curve, equivalent to 7680-bit RSA
        { value: "P-521", label: "P-521" }, // NIST P-521 curve, equivalent to 15360-bit RSA
    ]

    const shaAlgorithmOptions: MultiSelectOption[] = [
        { value: "SHA256", label: "SHA256" },
        { value: "SHA384", label: "SHA384" },
        { value: "SHA512", label: "SHA512" }
    ];

    const handleEncryptionTypeChange = (encryptionType: EncryptionTypes) => {
        setFieldValue("encryptionType", encryptionType);
        setFieldValue("keyParameter", "");
    }

    const handleCaCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue("isCA", e.target.checked);

        if (e.target.checked) {
            setFieldValue("certificateAuthorityId", "");
            setFieldValue("certificateAuthorityLabel", "");
        } else {
            setFieldValue("certificateTypeId", "");
            setFieldValue("certificateTypeLabel", "");
        }
    };

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setStepsDescription(prev => ({
            ...prev,
            encryption: `${values.encryptionType} - ${values.keyParameter}`
        }));
        onNext(e);
    }

    const isValid = (): boolean => {
        return values.encryptionType && values.keyParameter && values.shaAlgorithm && (!values.isCA ? values.certificateAuthorityId : true);
    }

    return <WizardStepLayout onBack={onBack} onNext={onLocalNext} nextDisabled={!isValid()}>
        <span className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`}>{translate.byKey(TranslationKeys.encryption)}</span>

        <div className='flex flex-col space-y-2'>
            {/*  Encryption Type */}
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between">
                    <Label htmlFor="encryptionType"
                        className="text-xs text-black mt-1 mb-1"
                        required>{translate.byKey(TranslationKeys.encryptionType)}:</Label>
                    {errors.encryptionType ? <span className='text-red-600 text-xs'>{errors.encryptionType}</span> : null}
                </div>
                <div className="flex flex-row space-x-4">
                    <div className="flex items-center text-sm font-medium">
                        <Field
                            id="rsa-checkbox"
                            type="checkbox"
                            name="encryptionType"
                            value={EncryptionTypes.RSA}
                            checked={values.encryptionType === EncryptionTypes.RSA}
                            onChange={() => handleEncryptionTypeChange(EncryptionTypes.RSA)}
                            className="mr-1" />
                        <Label htmlFor="rsa-checkbox" className="pl-1 text-sm">{translate.byKey(TranslationKeys.rsa)}</Label>
                    </div>
                    <div className="flex items-center text-sm font-medium">
                        <Field
                            id="ec-checkbox"
                            type="checkbox"
                            name="encryptionType"
                            value={EncryptionTypes.EC}
                            checked={values.encryptionType === EncryptionTypes.EC}
                            onChange={() => handleEncryptionTypeChange(EncryptionTypes.EC)}
                            className="mr-1" />
                        <Label htmlFor="ec-checkbox" className="pl-1 text-sm">{translate.byKey(TranslationKeys.ecParam)}</Label>
                    </div>
                </div>
            </div>
            {/* Key Parameter Field */}
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between mr-6">
                    <Label
                        htmlFor="keyParameter"
                        className="text-xs text-black mt-1 mb-1" required>
                        {values.encryptionType === EncryptionTypes.RSA ? translate.byKey(TranslationKeys.keySize) : translate.byKey(TranslationKeys.curve)}
                    </Label>
                    {errors.keyParameter ? <span className='text-red-600 text-xs'>{errors.keyParameter}</span> : null}
                </div>
                <SettingSelectField
                    required
                    accessor={`keyParameter`}
                    options={values.encryptionType === EncryptionTypes.RSA ? rsaKeySizesOptions : ecCurvesOption} />
            </div>
            {/* SHA Algorithm Field */}
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between mr-6">
                    <Label
                        htmlFor="shaAlgorithm"
                        className="text-xs text-black mt-1 mb-1" required>
                        {translate.byKey(TranslationKeys.shaAlgorithm)}
                    </Label>
                    {errors.shaAlgorithm ? <span className='text-red-600 text-xs'>{errors.shaAlgorithm}</span> : null}
                </div>
                <SettingSelectField required accessor={`shaAlgorithm`} options={shaAlgorithmOptions} />
            </div>

            {/* Is Certificate Authority */}
            <div className="flex flex-row flex-1 pr-3 text-sm font-medium">
                <Field
                    id="ca-checkbox"
                    type="checkbox"
                    name="isCA"
                    checked={values.isCA || false}
                    onChange={handleCaCheckboxChange}
                    className="mr-1" />
                <Label htmlFor="ca-checkbox" className="pl-1 text-sm">{translate.byKey(TranslationKeys.generateAsCertificateAuthority)}</Label>
            </div>
            {/* Certificate Authority */}
            <div className="flex flex-col flex-1 pr-3 mr-6">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="certificateAuthority"
                        className="text-xs text-black mt-1" required={!values.isCA}>
                        {translate.byKey(TranslationKeys.certificateAuthority)}:
                    </Label>
                    {errors.certificateAuthorityId && !values.isCA ? <span className='text-red-600 text-xs'>{errors.certificateAuthorityId}</span> : null}
                </div>
                <div id="certificateAuthorityDropDownField">
                    <PaginatedSearchableSelectField
                        name="Certificate Authority"
                        accessor='certificateAuthorityId'
                        key={"certificateAuthoritySelectField"}
                        dataMethod={(listParameters?: ListQuery) => getValidCertificateAuthorities(listParameters)}
                        searchAccessor={'name'}
                        required={!values.isCA}
                        disabled={values.isCA}
                        onSelect={(_, item) => {
                            setFieldValue('certificateAuthorityLabel', item?.label);
                            setFieldValue('certificateAuthorityId', item?.value);
                        }}
                        selectedItemLabel={values.certificateAuthorityLabel} />
                </div>
            </div>
        </div>
    </WizardStepLayout>
}

export default Encryption;